"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { WebAuthnAbortService } from "@simplewebauthn/browser";
import { useQueryClient } from "@tanstack/react-query";
import { KeySquare, Loader2 } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUser } from "@/hooks/useUser";
import { useAuth } from "@/lib/auth-client";
const formSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address"
  }),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters"
  })
});
function getSafeRedirectUrl(url: string | null): string {
  if (!url) {
    return "/";
  }
  if (url.startsWith("/") && !url.startsWith("//")) {
    return url;
  }
  return "/";
}
export default function Login() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const {
    signIn
  } = useAuth();
  const returnUrl = getSafeRedirectUrl(searchParams.get("returnUrl"));
  useUser({
    redirectTo: returnUrl,
    redirectWhen: "authenticated"
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  });
  const passkeyAutofillStarted = useRef(false);
  useEffect(() => {
    // Start the conditional (autofill) passkey ceremony exactly once. Re-running
    // it restarts the WebAuthn request, which flickers the browser/password
    // manager prompt and aborts an in-progress manual passkey button click.
    if (passkeyAutofillStarted.current) {
      return;
    }
    if (typeof window === "undefined" || !window.PublicKeyCredential) {
      return;
    }
    passkeyAutofillStarted.current = true;
    void signIn.passkey({
      autoFill: true
    }).then(res => {
      if (res?.data) {
        queryClient.clear();
        router.push(returnUrl);
      } else if (res?.error) {
        if (res.error.message?.toLowerCase().includes("cancelled")) {
          return;
        }
        toast.error(res.error.message ?? "Failed to sign in with passkey");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function handlePasskeySignIn() {
    setIsLoading(true);
    try {
      // Cancel the pending conditional (autofill) ceremony started on mount so
      // it doesn't collide with this modal request and abort it as "cancelled".
      WebAuthnAbortService.cancelCeremony();
      const res = await signIn.passkey();
      if (res?.error) {
        toast.error(res.error.message ?? "Failed to sign in with passkey");
        return;
      }
      queryClient.clear();
      toast.success("Login successful");
      router.push(returnUrl);
    } catch (error: unknown) {
      toast.error((error as Error)?.message || "Failed to sign in with passkey");
    } finally {
      setIsLoading(false);
    }
  }
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    // Abort the pending conditional (autofill) passkey ceremony so it can't pop a
    // native passkey/biometric prompt after a successful email sign-in + redirect.
    WebAuthnAbortService.cancelCeremony();
    const {
      error
    } = await signIn.email({
      email: values.email,
      password: values.password
    }, {
      onSuccess: () => {
        queryClient.clear();
        toast.success("Login successful");
        router.push("/");
      },
      onError: ctx => {
        toast.error(ctx.error.message ?? "An unknown error occurred", {
          style: {
            backgroundColor: "var(--destructive)",
            color: "var(--destructive-foreground)"
          }
        });
      }
    });
    if (error) {
      toast.error(error.message ?? "An unknown error occurred", {
        style: {
          backgroundColor: "var(--destructive)",
          color: "var(--destructive-foreground)"
        }
      });
    }
    setIsLoading(false);
  }
  return <div className="px-4 sm:px-0 max-w-[64rem] mx-auto flex h-screen w-screen flex-col items-center justify-center">
			<div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
				<div className="flex flex-col space-y-2 text-center">
					<h1 className="text-2xl font-semibold tracking-tight">
						Welcome back
					</h1>
					<p className="text-sm text-muted-foreground">
						Enter your email and password to sign in to your account
					</p>
				</div>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField control={form.control} name="email" render={({
            field
          }) => <FormItem>
									<FormLabel>Email</FormLabel>
									<FormControl>
										<Input placeholder="name@example.com" type="email" autoComplete="username webauthn" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>} />
						<FormField control={form.control} name="password" render={({
            field
          }) => <FormItem>
									<FormLabel>Password</FormLabel>
									<FormControl>
										<Input placeholder="••••••••" type="password" autoComplete="current-password webauthn" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>} />
						<Button type="submit" className="w-full" disabled={isLoading}>
							{isLoading ? <>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Signing in...
								</> : "Sign in"}
						</Button>
					</form>
				</Form>
				<div className="relative">
					<div className="absolute inset-0 flex items-center">
						<span className="w-full border-t" />
					</div>
					<div className="relative flex justify-center text-xs uppercase">
						<span className="bg-background px-2 text-muted-foreground">Or</span>
					</div>
				</div>
				<Button onClick={handlePasskeySignIn} variant="outline" className="w-full" disabled={isLoading}>
					{isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeySquare className="mr-2 h-4 w-4" />}
					Sign in with passkey
				</Button>
			</div>
		</div>;
}